import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	IStructureAPI,
	StructureAPI,
	LayoutDoneServiceSymbol,
	ILayoutDoneService,
	LoggerSymbol,
	ILogger,
	ExperimentsSymbol,
	Experiments,
} from '@wix/thunderbolt-symbols'
import { name } from './symbols'
import type { IDetachApi, ViewerManagerRuntimeLayoutApiEditorConfig } from './types'
import {
	getDisplayedId,
	getFullId,
	getItemId,
	isDisplayedOnly,
	isRepeater,
	getRuntimeStyleOverridesManager,
} from '@wix/thunderbolt-commons'
import type { CssOverrides } from '@wix/thunderbolt-commons'
import { IRootScrollApi, RootScrollApiSymbol } from 'ds-feature-viewer-manager-scroll-api'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'

type MeasureBoundingRect = {
	left: number
	top: number
	width: number
	height: number
}

type ContainersMeasure = {
	gridMeasures?: {
		gridTemplateRows: string
		gridTemplateColumns: string
	}
}

type DetachedComps = { [compId: string]: MeasureBoundingRect }

type PageMeasures = { top: number; minHeight: number }

const getInlineContentSelector = (compId: string) => `[data-mesh-id=${compId}inlineContent-gridContainer]`

export const DetachApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		LayoutDoneServiceSymbol,
		StructureAPI,
		RootScrollApiSymbol,
		LoggerSymbol,
		MeasuresApiSymbol,
		ExperimentsSymbol,
	],
	(
		config: ViewerManagerRuntimeLayoutApiEditorConfig,
		window: NonNullable<BrowserWindow>,
		layoutDoneService: ILayoutDoneService,
		structureAPI: IStructureAPI,
		rootScrollApi: IRootScrollApi,
		logger: ILogger,
		measuresApi: IMeasuresApi,
		experiments: Experiments
	): IDetachApi => {
		const runtimeStyleOverridesManager = getRuntimeStyleOverridesManager()
		let detachedComps: DetachedComps = {}
		let pageMeasures: PageMeasures = { top: 0, minHeight: 0 }

		const measureBoundingRect = (compId: string) => {
			const element = measuresApi.getCompElement(compId)
			const compBoundingClientRect = element?.getBoundingClientRect()

			return {
				left: compBoundingClientRect?.left || 0,
				top: compBoundingClientRect?.top || 0,
				width: element?.offsetWidth || 0,
				height: element?.offsetHeight || 0,
			}
		}

		const getAncestorsDisplayedIds = (compId: string) => {
			const isRepeatedComponent = isDisplayedOnly(compId)
			if (!isRepeatedComponent) {
				return config.compsData[compId].ancestors
			}
			const nonInflatedAncestors = config.compsData[getFullId(compId)].ancestors
			const itemId = getItemId(compId)
			let shouldInflateId = true
			// Need to inflate only components that are inside the repeater
			return nonInflatedAncestors.map((ancestor) => {
				if (isRepeater(structureAPI.get(ancestor).componentType)) {
					shouldInflateId = false
				}
				if (shouldInflateId) {
					return getDisplayedId(ancestor, itemId)
				}
				return ancestor
			})
		}

		const getCompsBoundingRect = (compIds: Array<string>) => {
			const compsBoundingRects: { [compId: string]: MeasureBoundingRect } = {}

			compIds.forEach((compId: string) => {
				compsBoundingRects[compId] = measureBoundingRect(compId)
			})

			return compsBoundingRects
		}

		const measureContainer = (compId: string): ContainersMeasure => {
			const containerNode = measuresApi.getCompElement(compId)
			if (containerNode) {
				const contentWrapper = containerNode.querySelector(getInlineContentSelector(compId))
				const contentWrapperComputedStyle = contentWrapper && window.getComputedStyle(contentWrapper)
				const display = contentWrapperComputedStyle?.display

				return {
					...(display === 'grid' && {
						gridMeasures: {
							gridTemplateRows: contentWrapperComputedStyle!.gridTemplateRows,
							gridTemplateColumns: contentWrapperComputedStyle!.gridTemplateColumns,
						},
					}),
				}
			}

			return {}
		}

		const getPlaceholderOverrides = (
			ancestorsToLock: Array<string>,
			compsBoundingRects: Record<string, MeasureBoundingRect>,
			ancestorsMeasuresForPlaceholders: Record<string, ContainersMeasure>
		) => {
			const placeholderOverrides: Array<{ selector: string; css: CssOverrides }> = []

			ancestorsToLock.forEach((containerId) => {
				const containerBoundingRect = compsBoundingRects[containerId]
				const measures = ancestorsMeasuresForPlaceholders[containerId]

				placeholderOverrides.push({
					selector: `#${containerId}`,
					css: {
						width: `${containerBoundingRect.width}px`,
						height: `${containerBoundingRect.height}px`,
					},
				})

				if (measures.gridMeasures) {
					placeholderOverrides.push({
						selector: `#${containerId} ${getInlineContentSelector(containerId)}`,
						css: {
							'grid-template-rows': measures.gridMeasures.gridTemplateRows,
							'grid-template-columns': measures.gridMeasures.gridTemplateColumns,
						},
					})
				}
			})

			return placeholderOverrides
		}

		const calcPageMeasures = (pageId: string) => {
			const element = measuresApi.getCompElement(pageId)

			if (element) {
				const style = window.getComputedStyle(element)

				const minHeight = parseInt(style.getPropertyValue('min-height'), 10)
				const top = element.getBoundingClientRect()!.top

				pageMeasures = { top, minHeight }
			}
		}

		const startDetach = (compIds: Array<string>) => {
			// disable notification of onRenderDone
			layoutDoneService.enableLayoutDone(false)

			const ancestorsToLock = [...new Set(compIds.flatMap((compId) => getAncestorsDisplayedIds(compId)))]
			const compsBoundingRects = getCompsBoundingRect(ancestorsToLock.concat(compIds))

			// measure gridTemplateRows and gridTemplateColumns of grid ancestors
			const ancestorsMeasuresForPlaceholders = ancestorsToLock.reduce((result, containerId) => {
				result[containerId] = measureContainer(containerId)
				return result
			}, {} as Record<string, ContainersMeasure>)

			// get css overrides of locked ancestors
			const placeholderOverrides = getPlaceholderOverrides(
				ancestorsToLock,
				compsBoundingRects,
				ancestorsMeasuresForPlaceholders
			)

			compIds.forEach((compId) => {
				const compBoundingRect = compsBoundingRects[compId]

				placeholderOverrides.push({
					selector: `#${measuresApi.getCompIdSelectorForMesh(compId)}`,
					css: {
						position: 'fixed',
						left: `${compBoundingRect.left}px`,
						top: `${compBoundingRect.top}px`,
						width: `${compBoundingRect.width}px`,
						height: `${compBoundingRect.height}px`,
						margin: '0',
					},
				})

				const pageId = config.compsData[getFullId(compId)].pageId
				if (pageId) {
					calcPageMeasures(pageId)
				}

				detachedComps[compId] = compBoundingRect
			})

			placeholderOverrides.forEach((override) =>
				runtimeStyleOverridesManager.setItemCssOverrides(override.css, override.selector, window)
			)
		}

		const setPageMinHeight = (pageId: string, detachedCompBottom: number) => {
			// We should not increase page min height if the `enablePageAutoGrowForDetachedComponents`
			// flag is off
			if (!config.shouldEnablePageGrow) {
				return
			}

			const minHeight = Math.max(pageMeasures.minHeight, detachedCompBottom - pageMeasures.top)
			const pageCss = { 'min-height': `${minHeight}px` }

			runtimeStyleOverridesManager.setItemCssOverrides(pageCss, `#${pageId}`, window)
		}

		const api: IDetachApi = {
			detach: (compId) => api.detachMulti([compId]),
			detachMulti: (compIds: Array<string>) => {
				if (Object.keys(detachedComps).length > 0) {
					return
				}

				logger.interactionStarted('detach', { customParams: { compIds } })
				startDetach(compIds)
			},
			updateDetached: (compId, boundingBox) => {
				const detachedComp = detachedComps[compId]
				if (!detachedComp) {
					return
				}

				const positions = {
					top: 0,
					left: boundingBox.left,
				}

				if (boundingBox.absoluteTop !== undefined) {
					positions.top = boundingBox.absoluteTop
				} else if (boundingBox.top !== undefined) {
					const scrollY = rootScrollApi.getRootScrollForComp(compId).y || 0
					positions.top = boundingBox.top - scrollY
				} else {
					throw new Error('Both "top" nor "absoluteTop" are provided')
				}

				if (experiments['specs.thunderbolt.detachedApiScrollLeft']) {
					if (boundingBox.absoluteLeft !== undefined) {
						positions.left = boundingBox.absoluteLeft
					} else if (boundingBox.left !== undefined) {
						const scrollX = rootScrollApi.getRootScrollForComp(compId).x || 0
						positions.left = boundingBox.left - scrollX
					} else {
						throw new Error('Both "left" nor "absoluteLeft" are provided')
					}
				}

				const css = {
					top: `${positions.top}px`,
					left: `${positions.left}px`,
				}
				runtimeStyleOverridesManager.setItemCssOverrides(
					css,
					`#${measuresApi.getCompIdSelectorForMesh(compId)}`,
					window
				)

				const pageId = config.compsData[getFullId(compId)].pageId
				if (pageId) {
					setPageMinHeight(pageId, detachedComp.height + positions.top)
				}
			},
			clearDetached: () => {
				layoutDoneService.registerToRenderDone(
					() => {
						logger.interactionEnded('detach', { customParams: { compIds: Object.keys(detachedComps) } })
					},
					{ once: true }
				)
				layoutDoneService.enableLayoutDone(true)
				runtimeStyleOverridesManager.clearAllItemsCssOverrides(window)

				detachedComps = {}
				pageMeasures = { top: 0, minHeight: 0 }
			},
		}

		return api
	}
)
